import { IDetalleCedulaItem } from "../../../interfaces/ISeleccion";

/**
 * "Aplicar sugerencias de IA": toma las discrepancias que dejó la validación de
 * cédula (lo que dice la ficha vs. lo que leyó la IA del documento) y calcula
 * cómo quedan los campos editables del modal si se acepta lo leído.
 *
 * Campos que reporta la validación (ver cyber-service validarCedulaAsync):
 * run, nombres, apellidoPaterno, apellidoMaterno, fechaNacimiento,
 * fechaVencimiento, sexo ("M"/"F") y nacionalidad (ISO alfa-3, "CHL").
 * El RUN y la fecha de vencimiento no se editan desde el modal: quedan como
 * "no aplicadas" con su motivo.
 */

export interface IEstadoIdentidad {
  names: string;
  lastnames: string;
  birthdate: string;
  generoId: string;
  nacionalidadId: string;
}

export interface IOpcionCatalogo {
  code: string;
  name: string;
  /** Solo nacionalidades: código ISO alfa-3 del país. */
  alfa3?: string | null;
}

export interface ISugerenciaAplicada {
  campo: string;
  etiqueta: string;
  anterior: string;
  nuevo: string;
}

export interface ISugerenciaOmitida {
  campo: string;
  etiqueta: string;
  valorDocumento: string | null;
  motivo: string;
}

export interface IResultadoSugerenciasIA {
  estado: IEstadoIdentidad;
  aplicadas: ISugerenciaAplicada[];
  omitidas: ISugerenciaOmitida[];
}

const ETIQUETAS_CAMPO: Record<string, string> = {
  run: "RUT",
  rut: "RUT",
  nombres: "Nombre(s)",
  apellidoPaterno: "Apellido paterno",
  apellidoMaterno: "Apellido materno",
  fechaNacimiento: "Fecha de nacimiento",
  fechaVencimiento: "Fecha de vencimiento",
  sexo: "Género",
  nacionalidad: "Nacionalidad",
};

export const etiquetaCampoCedula = (campo: string): string => ETIQUETAS_CAMPO[campo] ?? campo;

/** Mayúsculas, sin tildes y sin espacios repetidos: para comparar, no para mostrar. */
const comparable = (v: string | null | undefined): string =>
  String(v ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim().toUpperCase();

const limpio = (v: string | null | undefined): string => String(v ?? "").replace(/\s+/g, " ").trim().toUpperCase();

/** Fecha leída a ISO (YYYY-MM-DD). Acepta ISO, DD-MM-YYYY, DD/MM/YYYY y YYYYMMDD. */
export const normalizarFechaIA = (v: string | null | undefined): string | null => {
  const s = String(v ?? "").trim();
  let y: string, m: string, d: string;
  let match: RegExpMatchArray | null;
  if ((match = s.match(/^(\d{4})-(\d{2})-(\d{2})/))) [, y, m, d] = match;
  else if ((match = s.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})$/))) [, d, m, y] = match;
  else if ((match = s.match(/^(\d{4})(\d{2})(\d{2})$/))) [, y, m, d] = match;
  else return null;
  const fecha = new Date(`${y}-${m}-${d}T00:00:00Z`);
  if (isNaN(fecha.getTime()) || fecha.getUTCMonth() + 1 !== Number(m) || fecha.getUTCDate() !== Number(d)) return null;
  return `${y}-${m}-${d}`;
};

/**
 * Reemplaza el apellido paterno (primera aparición) o materno (última) dentro
 * de "Apellidos", que en la ficha es un solo texto. Primero busca el valor de
 * la ficha como palabra(s) completas; si no está, solo se atreve con el caso
 * simple de dos apellidos. Devuelve null cuando no puede ubicarlo sin adivinar.
 */
const reemplazarApellido = (apellidos: string, valorBase: string | null, nuevo: string, cual: "paterno" | "materno"): string | null => {
  const palabras = limpio(apellidos).split(" ").filter(Boolean);
  const comparables = palabras.map(comparable);
  const base = comparable(valorBase).split(" ").filter(Boolean);
  const nuevas = limpio(nuevo).split(" ").filter(Boolean);

  if (base.length > 0) {
    const coincidencias: number[] = [];
    for (let i = 0; i + base.length <= comparables.length; i++) {
      if (base.every((b, j) => comparables[i + j] === b)) coincidencias.push(i);
    }
    if (coincidencias.length > 0) {
      const i = cual === "paterno" ? coincidencias[0] : coincidencias[coincidencias.length - 1];
      return [...palabras.slice(0, i), ...nuevas, ...palabras.slice(i + base.length)].join(" ");
    }
  }

  // Sin el valor de la ficha a la vista: solo los casos sin ambigüedad.
  if (palabras.length === 2) {
    return cual === "paterno" ? [...nuevas, palabras[1]].join(" ") : [palabras[0], ...nuevas].join(" ");
  }
  if (palabras.length === 0 && cual === "paterno") return nuevas.join(" ");
  if (palabras.length === 1 && cual === "materno" && base.length === 0) return [palabras[0], ...nuevas].join(" ");
  return null;
};

const nombreOpcion = (opciones: IOpcionCatalogo[], code: string): string =>
  opciones.find((o) => o.code === code)?.name ?? (code || "—");

export const aplicarSugerenciasIA = (
  detalle: IDetalleCedulaItem[],
  actual: IEstadoIdentidad,
  catalogos: { genders: IOpcionCatalogo[]; nationalities: IOpcionCatalogo[] },
): IResultadoSugerenciasIA => {
  const estado: IEstadoIdentidad = { ...actual };
  const aplicadas: ISugerenciaAplicada[] = [];
  const omitidas: ISugerenciaOmitida[] = [];

  const omitir = (d: IDetalleCedulaItem, motivo: string) =>
    omitidas.push({ campo: d.campo, etiqueta: etiquetaCampoCedula(d.campo), valorDocumento: d.valorDocumento, motivo });
  const aplicar = (d: IDetalleCedulaItem, anterior: string, nuevo: string) => {
    if (limpio(anterior) === limpio(nuevo)) return; // ya estaba así (una tilde sí cuenta como cambio)
    aplicadas.push({ campo: d.campo, etiqueta: etiquetaCampoCedula(d.campo), anterior: anterior || "—", nuevo });
  };

  for (const d of detalle) {
    const leido = String(d.valorDocumento ?? "").trim();
    if (!leido) { omitir(d, "la IA no leyó un valor en el documento"); continue; }

    switch (d.campo) {
      case "run":
      case "rut":
        omitir(d, "el RUT no se modifica desde aquí (se gestiona por mesa de ayuda)");
        break;

      case "fechaVencimiento":
        omitir(d, "no es un dato editable de la ficha");
        break;

      case "nombres": {
        const nuevo = limpio(leido);
        aplicar(d, estado.names, nuevo);
        estado.names = nuevo;
        break;
      }

      case "apellidoPaterno":
      case "apellidoMaterno": {
        const cual = d.campo === "apellidoPaterno" ? "paterno" : "materno";
        const resultado = reemplazarApellido(estado.lastnames, d.valorBase, leido, cual);
        if (resultado === null) { omitir(d, "no se pudo ubicar dentro de los apellidos de la ficha"); break; }
        aplicar(d, d.valorBase ? limpio(d.valorBase) : "—", limpio(leido));
        estado.lastnames = resultado;
        break;
      }

      case "fechaNacimiento": {
        const nuevo = normalizarFechaIA(leido);
        if (!nuevo) { omitir(d, "la fecha leída no es válida"); break; }
        aplicar(d, estado.birthdate, nuevo);
        estado.birthdate = nuevo;
        break;
      }

      case "sexo": {
        const prefijo = comparable(leido) === "F" ? "FEM" : comparable(leido) === "M" ? "MASC" : comparable(leido);
        const opcion = catalogos.genders.find((g) => comparable(g.name).startsWith(prefijo));
        if (!opcion) { omitir(d, "el valor leído no está en el catálogo de géneros"); break; }
        aplicar(d, nombreOpcion(catalogos.genders, estado.generoId), opcion.name);
        estado.generoId = opcion.code;
        break;
      }

      case "nacionalidad": {
        const opcion = catalogos.nationalities.find((n) => comparable(n.alfa3) === comparable(leido));
        if (!opcion) { omitir(d, "el país leído no está en el catálogo de nacionalidades"); break; }
        aplicar(d, nombreOpcion(catalogos.nationalities, estado.nacionalidadId), opcion.name);
        estado.nacionalidadId = opcion.code;
        break;
      }

      default:
        omitir(d, "no es un dato editable de la ficha");
    }
  }

  return { estado, aplicadas, omitidas };
};

/** Observación sugerida tras aplicar: qué cambió (antes → después) y qué no se aplicó. */
export const comentarioSugerenciasIA = (aplicadas: ISugerenciaAplicada[], omitidas: ISugerenciaOmitida[]): string => {
  if (aplicadas.length === 0) return "";
  const cambios = aplicadas.map((a) => `${a.etiqueta}: ${a.anterior} → ${a.nuevo}`).join("; ");
  let texto = `Se aplicaron los cambios sugeridos por IA y se cambiaron los datos de ${cambios}.`;
  if (omitidas.length > 0) {
    texto += ` No se aplicó: ${omitidas.map((o) => `${o.etiqueta}${o.valorDocumento ? ` (documento: ${o.valorDocumento})` : ""}, ${o.motivo}`).join("; ")}.`;
  }
  return texto;
};
